import React, { Component } from 'react';
import PropTypes from 'prop-types';
import lodashCompact from 'lodash/fp/compact';

import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';

import { hasRoleAdmin } from 'common/userRoles';

import {
  SR_API_ACTION_ADMIN_UPDATE_ITEM,
  SR_API_ACTION_SUPRA_SHOWING_ADD_TOUR_FEEDBACK,
} from 'api/actionTypes';

import { DATA_TABLE_SOURCE_PARTNER_STATS_SUPRA_SHOWINGS } from 'common/dataTableConstants';

import {
  dataTable,
  actionDataTableRemoveItem,
  actionDataTableLoadNextPage,
  actionDataTableLoadItems,
} from 'client/helpers/dataTables';
import withInfiniteScroll from 'client/helpers/withInfiniteScroll';

import branch from 'client/core/branch';
import postWithState from 'client/actions/postWithState';
import PartnerActions from 'client/actions/Partner';

import SrSupraShowingTourFeedbackModal from 'client/components/SrSupraShowingTourFeedbackModal';
import SrText from 'client/components/SrText';
import SrTable from 'client/components/SrTable';

function getDataTableSettings({ listingManagementCompany, listingId, apartmentId, sinceDate }) {
  const dataTableSettings = {
    dataSource: DATA_TABLE_SOURCE_PARTNER_STATS_SUPRA_SHOWINGS,
    viewId: ['by', listingManagementCompany, listingId, apartmentId, sinceDate]
      .filter((x) => x)
      .join('-'),
    sort: {
      'supraShowings.inDate': 'desc',
    },
    count: 50,
    customParams: {
      partnerStatsSupraShowings: {
        listingManagementCompany,
        listingId,
        apartmentId,
        sinceDate,
      },
    },
  };

  return dataTableSettings;
}

@dataTable(getDataTableSettings)
@withInfiniteScroll({
  offset: 420,
})
@branch({
  currentUser: ['currentUser'],
})
class SrDataTablePartnerStatSupraShowings extends Component {
  static propTypes = {
    items: PropTypes.arrayOf(PropTypes.object.isRequired).isRequired,
    dataTableIsFullyLoaded: PropTypes.bool.isRequired,
    onInfiniteScrollWrapperRef: PropTypes.func.isRequired,
    onInfiniteScrollCallbackReady: PropTypes.func.isRequired,
    dispatch: PropTypes.func.isRequired,
    onRefreshStats: PropTypes.func.isRequired,
    listingId: PropTypes.number,
    apartmentId: PropTypes.number,
    currentUser: PropTypes.object.isRequired,
  };

  constructor(props) {
    super(props);

    this.state = {
      editingTourFeedbackItem: null,
    };

    const { onInfiniteScrollCallbackReady } = props;
    onInfiniteScrollCallbackReady(this._infiniteScrollCallback);
  }

  _infiniteScrollCallback = () => {
    const { dataTableIsFullyLoaded, dispatch } = this.props;
    if (!dataTableIsFullyLoaded) {
      dispatch(actionDataTableLoadNextPage, getDataTableSettings(this.props));
    }
  };

  _onHideSupraShowingClick = (e) => {
    const { items, dispatch, onRefreshStats, listingId, apartmentId } = this.props;
    if (e.target.dataset.id) {
      const supraShowingId = parseInt(e.target.dataset.id, 10);
      const item = items.find((item) => item.id === supraShowingId);

      if (
        !global.confirm(
          'Are you sure you want to hide the item ' +
            `"${[
              item.agentName,
              item.agentEmail,
              item.agentPhone,
              momentWithAustinTimezone(item.createdDate).format('L'),
            ].join(', ')}"?`,
        )
      ) {
        return;
      }

      dispatch(postWithState, {
        action: SR_API_ACTION_ADMIN_UPDATE_ITEM,
        payload: {
          table: 'supraShowings',
          where: {
            id: supraShowingId,
          },
          updates: {
            hiddenFromStats: true,
          },
        },
        onSuccess: () => {
          dispatch(actionDataTableRemoveItem, {
            dataSource: DATA_TABLE_SOURCE_PARTNER_STATS_SUPRA_SHOWINGS,
            entityId: supraShowingId,
          });
          onRefreshStats();
          dispatch(PartnerActions.getPartnerListingTourReviews, {
            listingId,
            apartmentId,
            forceRefresh: true,
          });
        },
      });
    }
  };

  _onClickTourFeedback = (item) => {
    this.setState({
      editingTourFeedbackItem: item,
    });
  };

  _onCloseTourFeedbackModal = () => {
    this.setState({
      editingTourFeedbackItem: null,
    });
  };

  _onSubmitTourFeedback = ({ tourFeedback }) => {
    const { dispatch, listingId, apartmentId } = this.props;
    const { editingTourFeedbackItem } = this.state;

    dispatch(postWithState, {
      action: SR_API_ACTION_SUPRA_SHOWING_ADD_TOUR_FEEDBACK,
      payload: {
        supraShowingId: editingTourFeedbackItem.id,
        tourFeedback: tourFeedback || null,
      },
      onSuccess: () => {
        const dataTableSettings = getDataTableSettings(this.props);
        dataTableSettings.customParams.partnerStatsSupraShowings.supraShowingId =
          editingTourFeedbackItem.id;
        dispatch(actionDataTableLoadItems, dataTableSettings);
        dispatch(PartnerActions.getPartnerListingTourReviews, {
          listingId,
          apartmentId,
          forceRefresh: true,
        });
        this.setState({
          editingTourFeedbackItem: null,
        });
      },
    });
  };

  render() {
    const { editingTourFeedbackItem } = this.state;
    const { items, onInfiniteScrollWrapperRef, currentUser } = this.props;

    const isAdmin = hasRoleAdmin(currentUser.roles);

    return (
      <>
        <div ref={onInfiniteScrollWrapperRef}>
          <SrTable
            headers={lodashCompact([
              { content: '#' },
              { content: 'name' },
              { content: 'email' },
              { content: 'phone' },
              { content: 'created date' },
              isAdmin && { content: 'feedback' },
              isAdmin && { content: 'options' },
            ])}
            rows={items.map(
              ({ id, agentName, agentEmail, agentPhone, inDate, tourFeedback }, i) => ({
                cells: lodashCompact([
                  {
                    content: `${items.length - i}`,
                  },
                  {
                    content: agentName,
                  },
                  {
                    content: agentEmail,
                  },
                  {
                    content: agentPhone,
                  },
                  {
                    content: momentWithAustinTimezone(inDate).format('L'),
                  },
                  isAdmin && {
                    content: (
                      <SrText
                        color={tourFeedback ? 'green' : 'blue300'}
                        cursor="pointer"
                        onClick={() => this._onClickTourFeedback({ id, tourFeedback })}
                      >
                        Write Tour Feedback
                      </SrText>
                    ),
                  },
                  isAdmin && {
                    content: (
                      <SrText
                        color="blue300"
                        cursor="pointer"
                        onClick={this._onHideSupraShowingClick}
                        data-id={id}
                      >
                        Hide
                      </SrText>
                    ),
                  },
                ]),
              }),
            )}
          />
        </div>
        {editingTourFeedbackItem && (
          <SrSupraShowingTourFeedbackModal
            supraShowing={editingTourFeedbackItem}
            onClose={this._onCloseTourFeedbackModal}
            onSubmit={this._onSubmitTourFeedback}
          />
        )}
      </>
    );
  }
}

SrDataTablePartnerStatSupraShowings.displayName = 'SrDataTablePartnerStatSupraShowings';

export default SrDataTablePartnerStatSupraShowings;
